import React, { useEffect, useState, useCallback } from "react";

import {
  PublicClientApplication,
  AuthenticationResult,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";

import {
  authenticationModeService,
  AuthModeResponse,
} from "services/authenticationMode";

interface MsalAuthContextValue {
  authMode: AuthModeResponse | null;
  authModeLoading: boolean;
  signInWithMicrosoft: () => void;
}

const defaultAuthMode: AuthModeResponse = {
  authMode: "local",
  isExternalProvider: false,
  protocol: "credentials",
  responseType: "password",
  grantType: "password",
  usePkce: false,
  authorityUrl: null,
  clientId: null,
  redirectUri: null,
  scopes: [],
};

export const MsalAuthContext = React.createContext<MsalAuthContextValue>({
  authMode: null,
  authModeLoading: true,
  signInWithMicrosoft: () => {},
});

interface Props {
  children: React.ReactNode;
  forceMaster?: boolean;
  onRedirectSuccess?: (result: AuthenticationResult) => void | Promise<void>;
}

export const MsalAuthProvider: React.FC<Props> = ({
  children,
  forceMaster = false,
  onRedirectSuccess,
}) => {
  const [authMode, setAuthMode] = useState<AuthModeResponse | null>(null);
  const [authModeLoading, setAuthModeLoading] = useState(!forceMaster);
  const [msalInstance, setMsalInstance] =
    useState<PublicClientApplication | null>(null);

  useEffect(() => {
    if (forceMaster) {
      setAuthMode(defaultAuthMode);
      setAuthModeLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const mode = await authenticationModeService.getAuthenticationMode();
        if (cancelled) return;
        setAuthMode(mode);

        if (
          mode.authMode === "azure_ad" &&
          mode.clientId &&
          mode.authorityUrl
        ) {
          const instance = new PublicClientApplication({
            auth: {
              clientId: mode.clientId,
              authority: `${mode.authorityUrl}/v2.0`,
              redirectUri: window.location.origin,
            },
            cache: {
              cacheLocation: "sessionStorage" as const,
            },
          });

          await instance.initialize();

          const redirectResult = await instance
            .handleRedirectPromise()
            .catch(() => null);

          if (redirectResult?.accessToken && onRedirectSuccess) {
            await onRedirectSuccess(redirectResult);
          }

          if (!cancelled) {
            setMsalInstance(instance);
          }
        }
      } catch {
        if (!cancelled) {
          setAuthMode(defaultAuthMode);
        }
      } finally {
        if (!cancelled) {
          setAuthModeLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [forceMaster, onRedirectSuccess]);

  const signInWithMicrosoft = useCallback(() => {
    if (!msalInstance || !authMode) return;

    msalInstance.loginRedirect({
      scopes: authMode.scopes,
      prompt: "select_account",
    });
  }, [msalInstance, authMode]);

  const contextValue: MsalAuthContextValue = {
    authMode,
    authModeLoading,
    signInWithMicrosoft,
  };

  const content = (
    <MsalAuthContext.Provider value={contextValue}>
      {children}
    </MsalAuthContext.Provider>
  );

  if (msalInstance) {
    return <MsalProvider instance={msalInstance}>{content}</MsalProvider>;
  }

  return content;
};
